import type { PickerOptions, PickerFileMetadata } from 'filestack-js/build/main/lib/picker'
import type { Client, ClientOptions } from 'filestack-js'
import { trackError, trackEvent } from './analytics'

async function load(): Promise<{ init: (key: string, options?: ClientOptions) => Client }> {
  return import(/* webpackChunkName: "Filestack" */'filestack-js')
}

export async function init() : Promise<Client> {
  const w = window as any

  if (!w.flexhireFilepicker) {
    try {
      const filestack = await load()
      w.flexhireFilepicker = filestack.init(process.env.NEXT_PUBLIC_FILESTACK_KEY ?? '')
    } catch (error) {
      trackError(error)
    }
  }

  return w.flexhireFilepicker
}

export async function preload() {
  load().catch(error => trackError(error))
}

const getDefaultPickerOptions: (options: PickerOptions) => PickerOptions = options => ({
  maxFiles: 1,
  accept: 'image/*',
  maxSize: 10 * 1024 * 1024, // 10 MB
  ...options,
  onUploadStarted: () => trackEvent('Filestack Upload Started'),
  onFileSelected: () => trackEvent('Filestack File Selected'),
  onFileCropped: () => trackEvent('Filestack File Cropped'),
  onClose: () => {
    trackEvent('Filestack Close')

    if (options.onClose) {
      options.onClose()
    }
  },
  onCancel: () => trackEvent('Filestack Cancel'),
  onFileUploadStarted: () => trackEvent('Filestack File Upload Started'),
  onFileUploadFinished: () => trackEvent('Filestack File Upload Finished'),
  onFileUploadCancel: () => trackEvent('Filestack File Upload Cancel'),
  onFileUploadFailed: (file, error) => {
    try {
      console.log(`File upload for file ${file?.filename || '?'} failed`)
      // NOTE: 'error' is actually a string with the filename. Figure out why
      trackError(new Error('File upload failed'), 'high', {
        filename: file?.filename || '?',
      })
    } catch (err) {
      trackError(err)
    }

    if (options.onFileUploadFailed) {
      options.onFileUploadFailed(file, error)
    }
  },
})

export async function pickAndStoreMultiple(options: PickerOptions = {}): Promise<PickerFileMetadata[]> {
  const filepicker = await init()

  return new Promise<PickerFileMetadata[]>((resolve) => {
    try {
      filepicker.picker({
        ...getDefaultPickerOptions(options),
        onUploadDone: (result) => {
          try {
            if (result.filesUploaded.length > 0) {
              resolve(result.filesUploaded)
            }

            trackEvent('Filestack Upload Completed')
          } catch (error) {
            // TODO figure out why the regular error tracking doesn't work here https://github.com/getsentry/sentry-javascript/issues/2546#issuecomment-703331367
            trackError(new Error('file upload failed'))
          }
        },
        onCancel: () => {
          resolve([])
        },
      }).open()
    } catch (error) {
      trackError(new Error('file upload failed'))
    }
  })
}

export async function pickAndStore(options: PickerOptions = {}): Promise<PickerFileMetadata | undefined> {
  const results = await pickAndStoreMultiple(options)

  // Note: there might be no results, causing this to return undefined
  // This is reflected in the return type of the function
  return results[0]
}

function isFilestackUrl(url: string) {
  return /https?:\/\/cdn\.filestackcontent\.com/.test(url)
}

function getFileStackId(url: string) {
  return url.slice(url.lastIndexOf('/') + 1)
}

/**
 * Optimizes a filestack CDN URL for smaller image file size
 * TODO: we should switch to using filestack's `adaptive` library instead or maybe store urls in the backend
 * @param src the filestack CDN url to optimize
 * @param opt optional settings for optimization
 * @returns the optimized URL or the original URL if it is not a filestack URL or if it is not optimizable
 */
export function optimizeSrc(src: string, opt?: { width: number }): string {
  if (!src || !isFilestackUrl(src) || !getFileStackId(src)) return src

  let resizeSection = ''

  if (opt?.width !== undefined && opt.width > 0) {
    // support SSR
    // TODO: this might possibly lead to hydration mismatches
    const dpr = (typeof window === 'undefined') ? 1 : window.devicePixelRatio

    resizeSection = `resize=w:${Math.round(opt.width * dpr)}/`
  }

  return `https://cdn.filestackcontent.com/${resizeSection}output=format:jpg/compress/${getFileStackId(src)}`
}

export function circleAvatarOptions(): PickerOptions {
  return {
    transformations: {
      circle: true,
      force: true,
    },
  }
}
